import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { vendorsApi } from '../services/api';

const VendorSelector = ({ 
  selectedVendor, 
  onVendorChange,
  disabled = false 
}) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Form state
  const [newVendorForm, setNewVendorForm] = useState({
    name: '',
    address: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async (searchTerm = '') => {
    try {
      setLoading(true);
      const response = searchTerm 
        ? await vendorsApi.search(searchTerm)
        : await vendorsApi.getAll();
      const data = response?.data;
      setVendors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError('Failed to fetch vendors');
      setVendors([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  const handleVendorSearch = (event, value, reason) => {
    if (reason === 'input') {
      fetchVendors(value);
    }
  };

  const handleCreateVendor = async () => {
    try {
      const response = await vendorsApi.create(newVendorForm);
      const newVendor = response.data?.vendor || response.data;
      
      // Add to list and select it
      setVendors(prev => [newVendor, ...prev]);
      onVendorChange(newVendor);
      
      // Reset form and close dialog
      setNewVendorForm({ name: '', address: '', phone: '', email: '' });
      setDialogOpen(false);
      
    } catch (err) {
      console.error('Error creating vendor:', err);
      setError('Failed to create vendor');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <StoreIcon color="primary" />
        <Typography variant="h6">Vendor</Typography>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Autocomplete
          fullWidth
          options={Array.isArray(vendors) ? vendors : []}
          getOptionLabel={(option) => option?.name || ''}
          value={selectedVendor}
          onChange={(event, newValue) => onVendorChange(newValue)}
          onInputChange={handleVendorSearch}
          disabled={disabled}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Vendor"
              placeholder="Search for a vendor..."
              helperText="Type to search for existing vendors"
              error={!!error}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box>
                <Typography variant="body1">{option.name}</Typography>
                {option.email && (
                  <Typography variant="body2" color="text.secondary">
                    {option.email}
                  </Typography>
                )}
                {option.phone && (
                  <Typography variant="body2" color="text.secondary">
                    {option.phone}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        />
        
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          disabled={disabled}
          sx={{ minWidth: 'auto', px: 2 }}
        >
          New
        </Button>
      </Box>
      
      {selectedVendor && (
        <Box sx={{ mt: 1 }}>
          <Chip
            icon={<StoreIcon />}
            label={selectedVendor.name}
            color="primary"
            variant="outlined"
          />
          {selectedVendor.email && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedVendor.email}
            </Typography>
          )}
          {selectedVendor.phone && (
            <Typography variant="body2" color="text.secondary">
              {selectedVendor.phone}
            </Typography>
          )}
        </Box>
      )}

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {/* Create Vendor Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Vendor</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Vendor Name"
              value={newVendorForm.name}
              onChange={(e) => setNewVendorForm(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newVendorForm.email}
              onChange={(e) => setNewVendorForm(prev => ({ ...prev, email: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Phone"
              value={newVendorForm.phone}
              onChange={(e) => setNewVendorForm(prev => ({ ...prev, phone: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={3}
              value={newVendorForm.address}
              onChange={(e) => setNewVendorForm(prev => ({ ...prev, address: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateVendor}
            variant="contained"
            disabled={!newVendorForm.name.trim()}
          >
            Create Vendor
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorSelector;
